import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, LogIn, Sparkles } from "lucide-react";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { EllieOrb } from "@/components/discovery/EllieOrb";
import { PrimaryButton } from "@/components/fionova/Primitives";
import { LegalNotice } from "@/components/fionova/LegalNotice";
import { useAuth } from "@/context/AuthContext";
import { journey } from "@/lib/journey";
import { getJourneyProgress } from "@/services/unsaidService";


const rise = (d) => ({ initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: d } });
// Quick Discovery is live only on the preview site until the owner approves launch.
const DISCOVER_ON = true;

// Homepage: two intentional pathways + existing-member login. (The retired short flow stays in DiscoveryConversation.jsx for rollback.)
export default function Home() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [guestStatus, setGuestStatus] = useState(null);

  useEffect(() => {
    if (user?.entitled) navigate("/app", { replace: true });
  }, [user, navigate]);

  // A journey already under way in this browser (or on this account) is offered back, never lost.
  useEffect(() => {
    if (loading || user || !journey.guestId()) return;
    getJourneyProgress().then((p) => setGuestStatus(p.status === "claimed" ? null : p.status || null));
  }, [loading, user]);

  if (loading || user === undefined || user?.entitled) return null;
  const first = (user?.name || "").split(" ")[0];
  const status = user ? user.journey_status : guestStatus;
  const cta = status === "completed" ? ["See my closing reflection", "/journey/complete"]
    : status === "active" ? ["Continue my journey with Ellie", "/journey"] : ["Take the Full Journey with Ellie", "/journey"];

  return (
    <OnboardingLayout testId="page-landing" contentClassName="justify-center py-6 sm:py-8">
      <div className="flex flex-col items-center text-center">
        <motion.div {...rise(0)}><EllieOrb size={104} testId="landing-ellie-avatar" /></motion.div>
        <motion.p {...rise(0.1)} className="eyebrow mt-6 text-[11px] text-teal-700">FioNova · Meet Ellie</motion.p>
        <motion.h1 {...rise(0.18)} className="mt-3 text-4xl font-bold leading-tight tracking-tight text-slate-900 sm:text-5xl" data-testid="home-title">
          {first ? `Welcome back, ${first}.` : "Hi, I'm Ellie."} <span className="fio-gradient-text">Let's make room for what matters to you.</span>
        </motion.h1>
        <motion.p {...rise(0.26)} className="mt-5 max-w-md text-base leading-relaxed text-slate-700">
          A calm, private space to say what's on your mind, understand it more clearly, and take your next step with intention.
        </motion.p>
      </div>

      <motion.div {...rise(0.36)} className="mx-auto mt-8 w-full max-w-md space-y-3">
        <PrimaryButton data-testid="home-full-journey" data-state={status || "new"} onClick={() => navigate(cta[1])}>
          {cta[0]} <ArrowRight className="h-4 w-4" />
        </PrimaryButton>
        <p className="text-center text-[13px] text-slate-500" data-testid="home-full-journey-note">
          {status ? "Your journey is saved — pick up right where you left off" : "About 10–15 minutes · No account needed to begin"}
        </p>

        <LegalNotice action="continuing with FioNova" testId="home-disclosure" className="pt-1" />
        {DISCOVER_ON ? (
          <div className="mt-6 text-center" data-testid="home-discover-section">
            <h2 className="text-lg font-semibold leading-snug tracking-tight text-slate-900" data-testid="home-discover-headline">What if just 8 questions could help you understand why you feel the way you do?</h2>
            <p className="mt-1.5 text-[14.5px] leading-relaxed text-slate-600">In about 2 minutes, Ellie helps you explore the emotional patterns you might not have noticed before.</p>
          <button onClick={() => navigate("/discover")} data-testid="home-quick-discovery"
            className="fio-glass fio-hairline mt-3 flex w-full items-center justify-between gap-3 rounded-full px-5 py-3.5 transition-shadow hover:ring-1 hover:ring-violet-300">
            <span className="flex items-center gap-2 text-[15px] font-semibold text-slate-800">
              <Sparkles className="h-4 w-4 text-violet-500" /> Quick Discovery
            </span>
            <span className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-500">About 2 minutes <ArrowRight className="h-3.5 w-3.5" /></span>
          </button>
            <p className="mt-2 text-xs text-slate-500" data-testid="home-discover-note">8 personalized questions. Free to begin.</p>
            <p className="mt-1 text-[11px] leading-relaxed text-slate-400" data-testid="home-discover-pricing">Unlocking your personalized insights requires a subscription: $2.99 for the first month, then $16.99/month until canceled, plus applicable taxes.</p>
          </div>
        ) : (
        <div className="fio-glass fio-hairline mt-4 flex items-center justify-between gap-3 rounded-full px-5 py-3.5 opacity-80" role="button" aria-disabled="true" aria-label="Quick Discovery, coming soon, not yet available" data-testid="home-quick-discovery">
          <span className="flex items-center gap-2 text-[15px] font-semibold text-slate-500">
            <Sparkles className="h-4 w-4 text-violet-400" /> Quick Discovery
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-testid="home-quick-discovery-soon">
            <Clock className="h-3 w-3" /> Coming soon
          </span>
        </div>
        )}
      </motion.div>

      <motion.div {...rise(0.46)} className="mx-auto mt-6 w-full max-w-md border-t border-slate-200/80 pt-5 text-center">
        {user ? (
          <button data-testid="home-member-continue" onClick={() => navigate(user.has_discovery ? "/discovery/reveal" : "/journey")}
            className="inline-flex items-center gap-2 text-[15px] font-semibold text-teal-700 transition-colors hover:text-teal-800">
            Continue where I left off <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button data-testid="home-member-login" onClick={() => navigate("/login")}
            className="inline-flex items-center gap-2 rounded-full bg-white/80 px-5 py-2.5 text-[15px] font-semibold text-slate-800 ring-1 ring-slate-200 transition-colors hover:text-teal-700">
            <LogIn className="h-4 w-4" /> Existing Member Login
          </button>
        )}
        <p className="mt-6 text-xs text-slate-500" data-testid="home-legal">
          <a href="https://getfionova.com/terms" target="_blank" rel="noopener noreferrer" data-testid="home-terms-link" className="underline underline-offset-2 hover:text-teal-700">Terms of Service</a>
          {" · "}
          <a href="https://getfionova.com/privacy" target="_blank" rel="noopener noreferrer" data-testid="home-privacy-link" className="underline underline-offset-2 hover:text-teal-700">Privacy Policy</a>
        </p>
      </motion.div>
    </OnboardingLayout>
  );
}
